/**
 * Monthly calendar (docs/04 §6).
 * Per-card timeline + flags; portfolio economics in two separate blocks.
 */

import { asCents, type Cents } from './money';
import {
  addDays,
  businessDate,
  compareBusinessDates,
  daysInMonth,
  onDayOfMonth,
  type BusinessDate,
} from './time';
import type { GraceStatus } from './grace';
import type { PaymentAllocationInput, StatementCycleInput } from './cycles';
import { remainingBalanceCents } from './obligations';
import type { CardPaymentInput } from './state';

const GRACE_ATTENTION = new Set(['inactive', 'unknown', 'restoration_pending']);
const SCHEDULED_STATUSES = new Set(['planned', 'scheduled']);

export type CalendarCardInput = {
  card_id: string;
  label: string;
  due_now_cents: Cents;
  autopay_expected_cents: Cents;
  not_yet_due_cents: Cents;
  grace_status: GraceStatus;
  grace_reason: string;
};

export type CalendarTransactionInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  kind: string;
  status: string;
  amount_cents: Cents;
  posted_date: string | null;
  transaction_date: string;
  expected_reward_cents: Cents | null;
  actual_reward_cents: Cents | null;
  recommendation_result_id: string | null;
};

export type CalendarPaymentInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  status: string;
  amount_cents: Cents;
  is_autopay: boolean;
  posted_date: string | null;
  scheduled_date: string | null;
  payment_date: string | null;
};

export type CalendarTimelineEvent = {
  kind:
    | 'cycle_close'
    | 'due_date'
    | 'scheduled_autopay'
    | 'planned_manual_payment'
    | 'projected_checking_withdrawal';
  date: BusinessDate;
  card_id: string;
  /** Visually distinguished when the cycle (or date) is projected. */
  is_projected: boolean;
  amount_cents: Cents | null;
  statement_balance_cents: Cents | null;
  remaining_due_cents: Cents | null;
  label: string;
};

export type CalendarCardFlags = {
  /** autopay_expected < due_now */
  payment_shortfall: boolean;
  /** grace inactive | unknown | restoration_pending */
  grace_attention: boolean;
  /** no autopay and no planned payment inside 7 days of due */
  uncovered_due: boolean;
  has_projected_dates: boolean;
};

export type CalendarCardRow = {
  card_id: string;
  label: string;
  due_now_cents: Cents;
  grace_status: GraceStatus;
  grace_reason: string;
  timeline: readonly CalendarTimelineEvent[];
  flags: CalendarCardFlags;
};

/**
 * Honest portfolio picture — never combined with recommender value (docs/04 §6).
 */
export type PortfolioEconomicsBlock = {
  purchases_cents: Cents;
  payments_cents: Cents;
  remaining_obligations_cents: Cents;
  next_cycle_purchases_cents: Cents;
  /** Σ actual_reward where present. */
  rewards_earned_cents: Cents;
  rewards_earned_label: 'actual';
  /** Σ expected_reward where actual is still null. */
  expected_rewards_unreconciled_cents: Cents;
  offers_statement_credits_cents: Cents;
  card_fees_cents: Cents;
  interest_charged_cents: Cents;
  net_portfolio_card_economics_cents: Cents;
};

/** Fair measure of the recommender — separate block only. */
export type RecommenderValueBlock = {
  value_generated_cents: Cents;
  value_generated_label: 'actual' | 'expected' | 'mixed';
};

export type MonthlyCalendar = {
  month_start: BusinessDate;
  month_end: BusinessDate;
  cards: readonly CalendarCardRow[];
  portfolio_economics: PortfolioEconomicsBlock;
  recommender_value: RecommenderValueBlock;
};

export type BuildMonthlyCalendarArgs = {
  asOf: BusinessDate;
  cards: readonly CalendarCardInput[];
  cycles: readonly StatementCycleInput[];
  payments: readonly CalendarPaymentInput[];
  transactions: readonly CalendarTransactionInput[];
  allocations: readonly PaymentAllocationInput[];
};

function monthBounds(asOf: BusinessDate): { start: BusinessDate; end: BusinessDate } {
  const y = Number(asOf.slice(0, 4));
  const m = Number(asOf.slice(5, 7));
  const start = onDayOfMonth(y, m, 1);
  const end = onDayOfMonth(y, m, daysInMonth(y, m));
  return { start, end };
}

function inMonth(date: BusinessDate, start: BusinessDate, end: BusinessDate): boolean {
  return compareBusinessDates(date, start) >= 0 && compareBusinessDates(date, end) <= 0;
}

function paymentAnchorDate(p: CalendarPaymentInput): BusinessDate | null {
  if (p.scheduled_date !== null) return businessDate(p.scheduled_date);
  if (p.payment_date !== null) return businessDate(p.payment_date);
  if (p.posted_date !== null) return businessDate(p.posted_date);
  return null;
}

function hasPlannedPaymentNearDue(
  cardId: string,
  due: BusinessDate,
  payments: readonly CalendarPaymentInput[],
): boolean {
  const windowStart = addDays(due, -7);
  for (const p of payments) {
    if (p.deleted_at !== null) continue;
    if (p.card_id !== cardId) continue;
    if (!SCHEDULED_STATUSES.has(p.status)) continue;
    const anchor = paymentAnchorDate(p);
    if (anchor === null) continue;
    if (compareBusinessDates(anchor, windowStart) >= 0 && compareBusinessDates(anchor, due) <= 0) {
      return true;
    }
  }
  return false;
}

function toCardPaymentInput(p: CalendarPaymentInput): CardPaymentInput {
  return {
    id: p.id,
    card_id: p.card_id,
    deleted_at: p.deleted_at,
    status: p.status,
    amount_cents: p.amount_cents,
    posted_date: p.posted_date,
  };
}

export function buildMonthlyCalendar(args: BuildMonthlyCalendarArgs): MonthlyCalendar {
  const { start: monthStart, end: monthEnd } = monthBounds(args.asOf);
  const paymentInputs = args.payments.map(toCardPaymentInput);

  const cardRows: CalendarCardRow[] = args.cards.map((meta) => {
    const timeline: CalendarTimelineEvent[] = [];
    let hasProjected = false;

    for (const cycle of args.cycles) {
      if (cycle.deleted_at !== null) continue;
      if (cycle.card_id !== meta.card_id) continue;

      const close = businessDate(cycle.cycle_end_date);
      if (inMonth(close, monthStart, monthEnd)) {
        if (cycle.is_projected) hasProjected = true;
        timeline.push({
          kind: 'cycle_close',
          date: close,
          card_id: meta.card_id,
          is_projected: cycle.is_projected,
          amount_cents: null,
          statement_balance_cents: cycle.statement_balance_cents,
          remaining_due_cents: null,
          label: cycle.is_projected ? 'Projected cycle close' : 'Cycle close',
        });
      }

      if (cycle.payment_due_date !== null) {
        const due = businessDate(cycle.payment_due_date);
        if (inMonth(due, monthStart, monthEnd)) {
          if (cycle.is_projected) hasProjected = true;
          const remaining = remainingBalanceCents(cycle, paymentInputs, args.allocations);
          timeline.push({
            kind: 'due_date',
            date: due,
            card_id: meta.card_id,
            is_projected: cycle.is_projected,
            amount_cents: remaining,
            statement_balance_cents: cycle.statement_balance_cents,
            remaining_due_cents: remaining,
            label: cycle.is_projected ? 'Projected due date' : 'Payment due',
          });
        }
      }

      if (cycle.autopay_scheduled_date !== null && cycle.autopay_scheduled_cents !== null) {
        const d = businessDate(cycle.autopay_scheduled_date);
        if (inMonth(d, monthStart, monthEnd)) {
          timeline.push({
            kind: 'scheduled_autopay',
            date: d,
            card_id: meta.card_id,
            is_projected: cycle.is_projected,
            amount_cents: cycle.autopay_scheduled_cents,
            statement_balance_cents: null,
            remaining_due_cents: null,
            label: 'Scheduled autopay',
          });
        }
      }
    }

    for (const p of args.payments) {
      if (p.deleted_at !== null) continue;
      if (p.card_id !== meta.card_id) continue;
      if (!SCHEDULED_STATUSES.has(p.status) && p.status !== 'processing') continue;
      const anchor = paymentAnchorDate(p);
      if (anchor === null || !inMonth(anchor, monthStart, monthEnd)) continue;

      if (p.is_autopay) {
        timeline.push({
          kind: 'scheduled_autopay',
          date: anchor,
          card_id: meta.card_id,
          is_projected: false,
          amount_cents: p.amount_cents,
          statement_balance_cents: null,
          remaining_due_cents: null,
          label: 'Scheduled autopay',
        });
      } else {
        timeline.push({
          kind: 'planned_manual_payment',
          date: anchor,
          card_id: meta.card_id,
          is_projected: false,
          amount_cents: p.amount_cents,
          statement_balance_cents: null,
          remaining_due_cents: null,
          label: 'Planned payment',
        });
      }
      timeline.push({
        kind: 'projected_checking_withdrawal',
        date: anchor,
        card_id: meta.card_id,
        is_projected: false,
        amount_cents: p.amount_cents,
        statement_balance_cents: null,
        remaining_due_cents: null,
        label: 'Projected checking withdrawal',
      });
    }

    timeline.sort((a, b) => {
      const c = compareBusinessDates(a.date, b.date);
      if (c !== 0) return c;
      return a.kind < b.kind ? -1 : a.kind > b.kind ? 1 : 0;
    });

    const paymentShortfall = meta.autopay_expected_cents < meta.due_now_cents;
    const graceAttention = GRACE_ATTENTION.has(meta.grace_status);

    // Earliest unpaid due in/near this month for uncovered-due check.
    let uncoveredDue = false;
    if (meta.autopay_expected_cents <= 0n && meta.due_now_cents > 0n) {
      const dueDates: BusinessDate[] = [];
      for (const cycle of args.cycles) {
        if (cycle.deleted_at !== null) continue;
        if (cycle.card_id !== meta.card_id) continue;
        if (cycle.is_projected) continue;
        if (cycle.payment_due_date === null) continue;
        dueDates.push(businessDate(cycle.payment_due_date));
      }
      dueDates.sort((a, b) => compareBusinessDates(a, b));
      const nextDue = dueDates[0];
      if (nextDue !== undefined) {
        uncoveredDue = !hasPlannedPaymentNearDue(meta.card_id, nextDue, args.payments);
      } else {
        uncoveredDue = true;
      }
    }

    return {
      card_id: meta.card_id,
      label: meta.label,
      due_now_cents: meta.due_now_cents,
      grace_status: meta.grace_status,
      grace_reason: meta.grace_reason,
      timeline,
      flags: {
        payment_shortfall: paymentShortfall,
        grace_attention: graceAttention,
        uncovered_due: uncoveredDue,
        has_projected_dates: hasProjected,
      },
    };
  });

  let purchases = 0n;
  let paymentsPosted = 0n;
  let rewardsActual = 0n;
  let rewardsExpectedOnly = 0n;
  let offersCredits = 0n;
  let fees = 0n;
  let interest = 0n;
  let recommenderActual = 0n;
  let recommenderExpected = 0n;

  for (const t of args.transactions) {
    if (t.deleted_at !== null) continue;
    if (t.status !== 'posted') continue;
    const posted = t.posted_date !== null ? businessDate(t.posted_date) : null;
    if (posted === null || !inMonth(posted, monthStart, monthEnd)) continue;

    if (t.kind === 'purchase') purchases += t.amount_cents;
    if (t.kind === 'fee') fees += t.amount_cents;
    if (t.kind === 'interest') interest += t.amount_cents;
    if (t.kind === 'statement_credit' || t.kind === 'refund') {
      // Refunds are not "offers"; statement_credit counts as offers/credits line.
      if (t.kind === 'statement_credit') offersCredits += t.amount_cents;
    }

    if (t.actual_reward_cents !== null) {
      rewardsActual += t.actual_reward_cents;
    } else if (t.expected_reward_cents !== null) {
      rewardsExpectedOnly += t.expected_reward_cents;
    }

    if (t.recommendation_result_id !== null) {
      if (t.actual_reward_cents !== null) recommenderActual += t.actual_reward_cents;
      else if (t.expected_reward_cents !== null) recommenderExpected += t.expected_reward_cents;
    }
  }

  for (const p of args.payments) {
    if (p.deleted_at !== null) continue;
    if (p.status !== 'posted') continue;
    if (p.posted_date === null) continue;
    const d = businessDate(p.posted_date);
    if (!inMonth(d, monthStart, monthEnd)) continue;
    paymentsPosted += p.amount_cents;
  }

  let remainingObligations = 0n;
  let nextCyclePurchases = 0n;
  for (const meta of args.cards) {
    remainingObligations += meta.due_now_cents;
    nextCyclePurchases += meta.not_yet_due_cents;
  }

  const net = rewardsActual + offersCredits - fees - interest;

  const recommenderTotal = recommenderActual + recommenderExpected;
  let recommenderLabel: RecommenderValueBlock['value_generated_label'] = 'expected';
  if (recommenderActual > 0n && recommenderExpected > 0n) recommenderLabel = 'mixed';
  else if (recommenderActual > 0n) recommenderLabel = 'actual';
  else recommenderLabel = 'expected';

  return {
    month_start: monthStart,
    month_end: monthEnd,
    cards: cardRows,
    portfolio_economics: {
      purchases_cents: asCents(purchases),
      payments_cents: asCents(paymentsPosted),
      remaining_obligations_cents: asCents(remainingObligations),
      next_cycle_purchases_cents: asCents(nextCyclePurchases),
      rewards_earned_cents: asCents(rewardsActual),
      rewards_earned_label: 'actual',
      expected_rewards_unreconciled_cents: asCents(rewardsExpectedOnly),
      offers_statement_credits_cents: asCents(offersCredits),
      card_fees_cents: asCents(fees),
      interest_charged_cents: asCents(interest),
      net_portfolio_card_economics_cents: asCents(net),
    },
    recommender_value: {
      value_generated_cents: asCents(recommenderTotal),
      value_generated_label: recommenderLabel,
    },
  };
}
